// PHASE_1 / B3 — the teams Content component (ADR-014 §2).
// Faz 8.6 / Batch A5 restyles the VISUAL layer to the novem language (serif
// PageHeader+em, a StatCard strip, Badge-toned kind/state cells, an Avatar lead
// cell inside a Card-wrapped table). The DATA layer is untouched: the SAME
// useKeysetQuery page from GET /v1/org/teams (BUILD_PLAN §5 keyset, never
// OFFSET). Teams are orthogonal to department (ADR-012). Allocation is a
// percent, never money.

import { useTranslation } from "react-i18next";

import {
  Avatar,
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  StatCard,
  type BadgeTone,
  type DataTableColumn,
} from "@novemai/components";
import { Crown, Users } from "lucide-react";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { formatCountWithMore } from "@/lib/count";
import { PersonLink } from "@/components/person-link";
import { fetchTeamsPage, type TeamRow } from "./api";

// SEM-301: the teams API now carries a server-resolved lead_name (PII-gated).
// The lead cell labels with the name, falling back to this short-id convention
// (A1/A2) only when the name is null (unresolved or tombstone-redacted).
const shortId = (id: string): string => id.slice(0, 8);

const STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  active: "success",
  archived: "neutral",
  draft: "warning",
};

export function OrgTeamsContent(): React.ReactElement {
  const { t } = useTranslation();

  const query = useKeysetQuery<TeamRow>({
    queryKey: ["org", "teams"],
    fetchPage: fetchTeamsPage,
  });

  // Honest aggregates over the loaded window ("+" while hasNextPage signals
  // further pages — the dashboard/A1 convention).
  const ledCount = query.items.filter((r) => r.lead_employee_id !== null).length;

  const columns: readonly DataTableColumn<TeamRow>[] = [
    {
      id: "name",
      header: t("org.teams_column_name"),
      cell: (row): React.ReactNode => (
        <span className="font-serif text-[15px] text-[var(--fg-primary)]">{row.name}</span>
      ),
    },
    {
      id: "kind",
      header: t("org.teams_column_kind"),
      cell: (row): React.ReactNode => (
        <Badge tone="info">{t(`org.team_kind.${row.kind}`)}</Badge>
      ),
    },
    {
      id: "lead",
      header: t("org.teams_column_lead"),
      cell: (row): React.ReactNode =>
        row.lead_employee_id !== null ? (
          <div className="flex items-center gap-2.5">
            <Avatar size={32} alt={row.lead_name ?? shortId(row.lead_employee_id)} />
            {/* The Avatar already carries the name as its accessible label, so
                only the text is wrapped — wrapping both would make the link
                announce the name twice. */}
            <PersonLink employeeId={row.lead_employee_id}>
              <span className="whitespace-nowrap font-mono text-[12px] text-[var(--fg-secondary)]">
                {row.lead_name ?? shortId(row.lead_employee_id)}
              </span>
            </PersonLink>
          </div>
        ) : (
          <span className="font-serif text-[13px] italic text-[var(--fg-muted)]">
            {t("org.teams_lead_unassigned")}
          </span>
        ),
    },
    {
      id: "state",
      header: t("org.teams_column_state"),
      cell: (row): React.ReactNode => (
        <Badge tone={STATE_TONE[row.state] ?? "neutral"}>
          {t(`org.team_state.${row.state}`)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              label={t("org.teams_kpi_total")}
              value={formatCountWithMore(query.items.length, query.hasNextPage)}
              tone="accent"
              icon={<Users size={18} />}
            />
            <StatCard
              label={t("org.teams_kpi_led")}
              value={formatCountWithMore(ledCount, query.hasNextPage)}
              tone="info"
              icon={<Crown size={18} />}
            />
          </div>

          <Card className="overflow-hidden">
            <DataTable<TeamRow>
              caption={t("org.teams_title")}
              columns={columns}
              rows={query.items}
              rowKey={(row): string => row.team_id}
              hasNextPage={query.hasNextPage}
              onLoadMore={query.fetchNextPage}
              isFetchingNextPage={query.isFetchingNextPage}
              loadMoreLabel={t("org.load_more")}
            />
          </Card>
        </>
      ) : (
        <Empty
          title={t("org.teams_empty_title")}
          description={t("org.teams_empty_description")}
        />
      )}
    </div>
  );
}
